import { SCORECARD_VERDICT_TONES } from "./constants";
import {
  COLUMN_AGGREGATE_TYPE_LABELS,
  getContainsComparisonMode,
  getContainsStaticValue,
  getScorecardAssertValidType,
  getScorecardCode,
  getScorecardCodeLanguage,
  getScorecardColumnAggregateType,
  getScorecardCompareJsonPath,
  getScorecardCompareOperator,
  getScorecardCompareStaticTarget,
  getScorecardCompareTargetMode,
  getScorecardCompareType,
  getScorecardCountBound,
  getScorecardCountType,
  getScorecardPercentile,
  getScorecardPrompt,
  getScorecardPromptSource,
  getScorecardRegexPattern,
  getTrajectoryExpectationMode,
  getTrajectoryExpectedScenarios,
  getTrajectoryExpectedSource,
  getTrajectoryMatchMode,
  getTrajectoryNoToolsExpected,
  isAgentTracePrimitive,
} from "./criterionConfig";
import type { ScorecardCriterionRowFilter, ScorecardRowVerdictFilter } from "./types";

import {
  SCORECARD_DEFAULT_THRESHOLDS,
  SCORECARD_VERDICT_LABELS,
  ScorecardPrimitiveType,
  ScorecardStatus,
  ScoreVerdict,
  type CriterionSummary,
  type EvaluatorResult,
  type GetScorecardResponse,
  type GetScorecardRowResponse,
  type ScorecardCalculation,
  type ScorecardRowSummary,
  type ScorecardStep,
  type ScorecardThresholds,
  type ScoreVerdict as ScoreVerdictType,
} from "@/queries/smart-tables/scorecard";
import type { SmartCell, SmartColumn } from "@/queries/smart-tables/types";
import { resolveCellDisplayState } from "@/routes/smart-table/utils/cellDisplayState";

export type ScorecardColumnReference = Pick<SmartColumn, "id" | "title">;

const PERCENT_MULTIPLIER = 100;

const formatCompactNumber = (value: unknown): string => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.0+$/, "");
  }
  if (typeof value === "string" && value.trim()) {
    return value;
  }
  return "—";
};

export const normalizeScorecardScore = (score: number | null | undefined): number | null => {
  if (typeof score !== "number" || Number.isNaN(score)) {
    return null;
  }
  return Math.max(0, Math.min(1, score));
};

export const formatScorecardScore = (score: number | null | undefined): string => {
  const normalizedScore = normalizeScorecardScore(score);
  if (normalizedScore === null) {
    return "—";
  }
  return `${(normalizedScore * PERCENT_MULTIPLIER).toFixed(1)}%`;
};

/** "p95" for 95, "p99.9" for 99.9. */
export const formatPercentileLabel = (percentile: unknown): string =>
  `p${formatCompactNumber(typeof percentile === "string" ? Number(percentile) : percentile)}`;

export const isColumnAggregatePrimitive = (primitiveType?: string | null): boolean =>
  primitiveType === ScorecardPrimitiveType.COLUMN_AGGREGATE;

/**
 * Human-readable summary of which column(s) a criterion runs on, e.g.
 * "Output", "Output vs Expected", or "Output contains Expected". Used to
 * surface this metadata in the overview list and detail views, since the
 * primitive type alone doesn't tell a user what data is being evaluated.
 */
const getColumnTitle = (columnId: string, columns: ScorecardColumnReference[]): string =>
  columns.find(column => column.id === columnId)?.title ?? "Deleted column";

export const getCriterionColumnsLabel = (
  step: Pick<ScorecardStep, "primitive_type" | "source_column_ids"> | undefined,
  columns: ScorecardColumnReference[],
): string | null => {
  const sourceColumnIds = step?.source_column_ids ?? [];
  if (sourceColumnIds.length === 0) {
    return null;
  }

  const titleFor = (columnId: string): string => getColumnTitle(columnId, columns);

  if (step?.primitive_type === ScorecardPrimitiveType.COMPARE && sourceColumnIds.length >= 2) {
    return `${titleFor(sourceColumnIds[0])} vs ${titleFor(sourceColumnIds[1])}`;
  }

  if (step?.primitive_type === ScorecardPrimitiveType.CONTAINS && sourceColumnIds.length >= 2) {
    return `${titleFor(sourceColumnIds[0])} contains ${titleFor(sourceColumnIds[1])}`;
  }

  return sourceColumnIds.map(titleFor).join(", ");
};

export const getColumnAggregateSourceColumnLabel = (
  step: Pick<ScorecardStep, "source_column_ids" | "primitive_config"> | undefined,
  columns: ScorecardColumnReference[],
): string | null => {
  const sourceColumnId =
    step?.source_column_ids?.[0] ??
    (typeof step?.primitive_config?.source === "string" ? step.primitive_config.source : undefined);
  return sourceColumnId ? getColumnTitle(sourceColumnId, columns) : null;
};

const quotePreviewValue = (value: string): string => `“${value || "Empty"}”`;

const parseConfiguredAssertions = (prompt: string): string[] => {
  try {
    const parsed = JSON.parse(prompt);
    if (Array.isArray(parsed)) {
      return parsed.map(assertion => String(assertion)).filter(Boolean);
    }
  } catch {
    // A non-JSON prompt is one assertion.
  }
  return prompt ? [prompt] : [];
};

const COMPARE_OPERATOR_PREVIEW_LABELS = {
  EQ: "Equals",
  NE: "Does not equal",
  GT: "Greater than",
  GTE: "Greater than or equal to",
  LT: "Less than",
  LTE: "Less than or equal to",
} as const;

const COUNT_TYPE_PREVIEW_LABELS = {
  chars: "characters",
  words: "words",
  sentences: "sentences",
  paragraphs: "paragraphs",
} as const;

export const getCriterionPreviewMetadata = (
  step: Pick<ScorecardStep, "primitive_type" | "source_column_ids" | "primitive_config">,
  columns: ScorecardColumnReference[],
): string[] => {
  const config = step.primitive_config ?? {};
  const columnTitle = (columnId: string): string => getColumnTitle(columnId, columns);

  switch (step.primitive_type) {
    case ScorecardPrimitiveType.COLUMN_AGGREGATE: {
      const aggregateType = getScorecardColumnAggregateType(config);
      if (aggregateType === "percentile_value") {
        return [`${formatPercentileLabel(getScorecardPercentile(config))} value`];
      }
      return [COLUMN_AGGREGATE_TYPE_LABELS[aggregateType]];
    }
    case ScorecardPrimitiveType.LLM_ASSERTION: {
      const promptSource = getScorecardPromptSource(config);
      if (promptSource) {
        return [`Assertions from ${columnTitle(promptSource)}`];
      }
      return parseConfiguredAssertions(getScorecardPrompt(config));
    }
    case ScorecardPrimitiveType.CONTAINS: {
      if (getContainsComparisonMode(config) === "column") {
        return [];
      }
      return [`Contains ${quotePreviewValue(getContainsStaticValue(config))}`];
    }
    case ScorecardPrimitiveType.COMPARE: {
      let target = "target column";
      if (getScorecardCompareTargetMode(config) === "static") {
        target = quotePreviewValue(getScorecardCompareStaticTarget(config));
      } else if (step.source_column_ids[1]) {
        target = columnTitle(step.source_column_ids[1]);
      }
      const compareType = getScorecardCompareType(config);
      if (compareType === "NUMBER") {
        return [
          `${COMPARE_OPERATOR_PREVIEW_LABELS[getScorecardCompareOperator(config)]} ${target}`,
        ];
      }
      if (compareType === "JSON") {
        return [`JSON path ${getScorecardCompareJsonPath(config)} matches ${target}`];
      }
      return [`Exact string match against ${target}`];
    }
    case ScorecardPrimitiveType.REGEX:
      return [`Matches /${getScorecardRegexPattern(config)}/`];
    case ScorecardPrimitiveType.ASSERT_VALID: {
      const type = getScorecardAssertValidType(config);
      let label = "number";
      if (type === "object") {
        label = "JSON object";
      } else if (type === "sql") {
        label = "SQL";
      }
      return [`Valid ${label}`];
    }
    case ScorecardPrimitiveType.CODE_EXECUTION: {
      const language = getScorecardCodeLanguage(config) === "JAVASCRIPT" ? "JavaScript" : "Python";
      const firstCodeLine = getScorecardCode(config)
        .split("\n")
        .map(line => line.trim())
        .find(Boolean);
      return [firstCodeLine ? `${language} · ${firstCodeLine}` : `${language} code check`];
    }
    case ScorecardPrimitiveType.COUNT: {
      const unit = COUNT_TYPE_PREVIEW_LABELS[getScorecardCountType(config)];
      const min = getScorecardCountBound(config, "min_count");
      const max = getScorecardCountBound(config, "max_count");
      if (min && max) {
        return [`Between ${min} and ${max} ${unit}`];
      }
      if (min) {
        return [`At least ${min} ${unit}`];
      }
      return max ? [`At most ${max} ${unit}`] : [`Counts ${unit}`];
    }
    case ScorecardPrimitiveType.MAX_TOOL_CALLS: {
      const maxCalls = config.max_calls;
      return [typeof maxCalls === "number" ? `At most ${maxCalls} tool calls` : "Tool call limit"];
    }
    case ScorecardPrimitiveType.NO_INVALID_TOOL_CALLS:
      return ["No invalid tool calls"];
    case ScorecardPrimitiveType.TRAJECTORY: {
      const modeLabel =
        getTrajectoryMatchMode(config) === "strict" ? "Exact sequence" : "Subsequence";
      if (getTrajectoryExpectationMode(config) === "column") {
        const expectedSource = getTrajectoryExpectedSource(config);
        return expectedSource ? [`${modeLabel} from ${columnTitle(expectedSource)}`] : [modeLabel];
      }
      if (getTrajectoryNoToolsExpected(config)) {
        return ["No tools should be called"];
      }
      return getTrajectoryExpectedScenarios(config)
        .filter(scenario => scenario.length > 0)
        .map(scenario => `${modeLabel}: ${scenario.join(" → ")}`);
    }
    case ScorecardPrimitiveType.TOOL_EVALUATOR:
      return ["Tool selection and arguments"];
    case ScorecardPrimitiveType.STRUCTURE:
      return ["Expected output structure"];
    default:
      return [];
  }
};

export const getCriterionMetadataLabel = (
  step:
    | Pick<ScorecardStep, "primitive_type" | "source_column_ids" | "primitive_config">
    | undefined,
  columns: ScorecardColumnReference[],
  options?: { isColumnAggregate?: boolean },
): string | null => {
  if (options?.isColumnAggregate || isColumnAggregatePrimitive(step?.primitive_type)) {
    const sourceColumnLabel = getColumnAggregateSourceColumnLabel(step, columns);
    return sourceColumnLabel ? `Column: ${sourceColumnLabel}` : null;
  }

  if (!step) {
    return null;
  }

  if (step.primitive_type === ScorecardPrimitiveType.CODE_EXECUTION) {
    return "Uses all table values";
  }

  const config = step.primitive_config ?? {};
  const configuredSourceId = typeof config.source === "string" ? config.source : "";
  const configuredTraceSourceId =
    typeof config.trace_source === "string" ? config.trace_source : "";
  const primarySourceId =
    step.source_column_ids[0] || configuredSourceId || configuredTraceSourceId;
  if (!primarySourceId) {
    return null;
  }

  const primarySourceLabel = getColumnTitle(primarySourceId, columns);

  if (step.primitive_type === ScorecardPrimitiveType.COMPARE) {
    const targetColumnId = step.source_column_ids[1];
    return targetColumnId
      ? `${primarySourceLabel} vs ${getColumnTitle(targetColumnId, columns)}`
      : `On ${primarySourceLabel}`;
  }

  if (step.primitive_type === ScorecardPrimitiveType.CONTAINS) {
    const valueSourceId =
      typeof config.value_source === "string" ? config.value_source : step.source_column_ids[1];
    return valueSourceId
      ? `${primarySourceLabel} contains ${getColumnTitle(valueSourceId, columns)}`
      : `On ${primarySourceLabel}`;
  }

  if (isAgentTracePrimitive(step.primitive_type)) {
    const traceSourceId =
      typeof config.trace_source === "string" ? config.trace_source : primarySourceId;
    return `Trace: ${getColumnTitle(traceSourceId, columns)}`;
  }

  return `On ${primarySourceLabel}`;
};

export const formatColumnAggregateResult = (
  aggregateResult?: Record<string, unknown> | null,
  fallbackAggregationType?: string,
): { primary: string; secondary: string | null; contributesToScore: boolean } | null => {
  if (!aggregateResult) {
    return null;
  }

  const aggregationType = aggregateResult.aggregation_type ?? fallbackAggregationType;
  const contributesToScore = aggregateResult.score_mode === "score";
  const total = typeof aggregateResult.total === "number" ? aggregateResult.total : undefined;
  const scoredRows =
    typeof aggregateResult.scored_rows === "number" ? aggregateResult.scored_rows : undefined;
  const excludedRows =
    typeof aggregateResult.excluded_rows === "number" ? aggregateResult.excluded_rows : undefined;

  if (aggregationType === "average_value") {
    const score = typeof aggregateResult.score === "number" ? aggregateResult.score : null;
    const secondary = [
      contributesToScore && score !== null ? `Score ${formatScorecardScore(score)}` : null,
      scoredRows !== undefined ? `${scoredRows} scored` : null,
      excludedRows && excludedRows > 0 ? `${excludedRows} skipped` : null,
    ]
      .filter(Boolean)
      .join(" · ");
    return {
      primary: formatCompactNumber(aggregateResult.average ?? aggregateResult.value),
      secondary: secondary || null,
      contributesToScore,
    };
  }

  if (aggregationType === "most_frequent_value") {
    const count = typeof aggregateResult.count === "number" ? aggregateResult.count : undefined;
    const percentage =
      typeof aggregateResult.percentage === "number"
        ? formatScorecardScore(aggregateResult.percentage)
        : null;
    const rowSupport =
      count !== undefined && total !== undefined ? `${count} of ${total} rows` : null;
    let secondary = rowSupport ? `Appears in ${rowSupport}` : null;
    if (contributesToScore) {
      secondary =
        [rowSupport, percentage ? `${percentage} score` : null].filter(Boolean).join(" · ") || null;
    }
    return {
      primary: formatCompactNumber(aggregateResult.winner ?? aggregateResult.value),
      secondary,
      contributesToScore,
    };
  }

  const rowsCompared =
    scoredRows !== undefined
      ? `${scoredRows} row${scoredRows === 1 ? "" : "s"}${
          excludedRows ? ` · ${excludedRows} skipped` : ""
        }`
      : null;

  if (aggregationType === "percentile_value") {
    return {
      primary: formatCompactNumber(aggregateResult.value),
      secondary:
        [formatPercentileLabel(aggregateResult.percentile), rowsCompared]
          .filter(Boolean)
          .join(" · ") || null,
      contributesToScore: false,
    };
  }

  if (aggregationType === "mean_absolute_error" || aggregationType === "root_mean_squared_error") {
    return {
      primary: formatCompactNumber(aggregateResult.value),
      secondary:
        [
          aggregationType === "mean_absolute_error" ? "MAE" : "RMSE",
          rowsCompared,
          typeof aggregateResult.max_absolute_error === "number"
            ? `largest miss ${formatCompactNumber(aggregateResult.max_absolute_error)}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ") || null,
      contributesToScore: false,
    };
  }

  if (aggregationType === "classification_report") {
    const correct = typeof aggregateResult.correct === "number" ? aggregateResult.correct : null;
    const accuracy = typeof aggregateResult.accuracy === "number" ? aggregateResult.accuracy : null;
    return {
      primary: formatScorecardScore(accuracy),
      secondary:
        [
          "Accuracy",
          correct !== null && scoredRows !== undefined
            ? `${correct} of ${scoredRows} correct`
            : null,
          typeof aggregateResult.macro_f1 === "number"
            ? `macro F1 ${formatScorecardScore(aggregateResult.macro_f1)}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ") || null,
      contributesToScore,
    };
  }

  if (aggregationType === "min_value" || aggregationType === "max_value") {
    const valueLabel = formatCompactNumber(aggregateResult.value);
    const winnerLabel = formatCompactNumber(aggregateResult.winner);
    const secondary =
      winnerLabel !== valueLabel && valueLabel !== "—" ? `Value ${valueLabel}` : null;
    return {
      primary: winnerLabel,
      secondary,
      contributesToScore: false,
    };
  }

  return null;
};

export interface ColumnAggregateTopValue {
  value: string;
  count: number;
  percentage: number | null;
}

export const getColumnAggregateTopValues = (
  aggregateResult?: Record<string, unknown> | null,
): ColumnAggregateTopValue[] => {
  const topValues = aggregateResult?.top_values;
  if (!Array.isArray(topValues)) {
    return [];
  }
  return topValues
    .filter(
      (entry): entry is Record<string, unknown> => typeof entry === "object" && entry !== null,
    )
    .map(entry => ({
      value: formatCompactNumber(entry.value),
      count: typeof entry.count === "number" ? entry.count : 0,
      percentage: typeof entry.percentage === "number" ? entry.percentage : null,
    }));
};

export const formatScorecardVerdictLabel = (verdict?: ScoreVerdictType | string | null): string => {
  if (!verdict) {
    return "—";
  }
  return SCORECARD_VERDICT_LABELS[verdict] ?? verdict;
};

export const formatScorecardDrift = (drift?: number | null): string | null => {
  if (typeof drift !== "number" || !Number.isFinite(drift)) {
    return null;
  }
  const points = Math.round(Math.abs(drift) * PERCENT_MULTIPLIER);
  if (points === 0) {
    return null;
  }
  return drift < 0 ? `- ${points}%` : `+ ${points}%`;
};

export const formatScorecardLastCalculated = (isoDate?: string | null): string | null => {
  if (!isoDate) {
    return null;
  }
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return `Last calculated ${date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })}`;
};

const getCompletedCalculationToolbarLabel = (
  calculation?: ScorecardCalculation | null,
): string | null => {
  if (!calculation || calculation.status !== ScorecardStatus.COMPLETED) {
    return null;
  }
  if (calculation.aggregate_verdict === ScoreVerdict.SKIPPED) {
    // Unrun / not-yet-computed scorecards can surface as skipped; present
    // this as setup guidance instead of a verdict.
    return "Setup";
  }
  const scoreLabel = formatScorecardScore(calculation.aggregate_score);
  const verdictLabel = formatScorecardVerdictLabel(calculation.aggregate_verdict);
  return `${scoreLabel} ${verdictLabel}`;
};

const getDisplayCalculationToolbarLabel = (
  scorecardData?: GetScorecardResponse | null,
): string | null =>
  getCompletedCalculationToolbarLabel(scorecardData?.display_calculation) ??
  getCompletedCalculationToolbarLabel(scorecardData?.latest_calculation);

export const getScorecardToolbarLabel = (scorecardData?: GetScorecardResponse | null): string => {
  const scorecard = scorecardData?.scorecard;
  if (!scorecard) {
    return "Scorecard";
  }

  if (scorecard.steps.length === 0) {
    return "Setup";
  }

  switch (scorecard.status) {
    case ScorecardStatus.NEEDS_SETUP:
      return "Needs setup";
    case ScorecardStatus.STALE:
      return getDisplayCalculationToolbarLabel(scorecardData) ?? "Stale";
    case ScorecardStatus.QUEUED:
    case ScorecardStatus.RUNNING:
      return "Calculating";
    case ScorecardStatus.FAILED:
      return getDisplayCalculationToolbarLabel(scorecardData) ?? "Error";
    case ScorecardStatus.READY:
      return getDisplayCalculationToolbarLabel(scorecardData) ?? "Ready";
    case ScorecardStatus.COMPLETED:
      return getDisplayCalculationToolbarLabel(scorecardData) ?? "Ready";
    default:
      return scorecard.name;
  }
};

export const isScorecardRecalculating = (scorecardData?: GetScorecardResponse | null): boolean => {
  const status = scorecardData?.scorecard?.status;
  return status === ScorecardStatus.QUEUED || status === ScorecardStatus.RUNNING;
};

export const isScorecardStale = (scorecardData?: GetScorecardResponse | null): boolean =>
  Boolean(scorecardData?.scorecard?.stale_state?.is_stale) ||
  scorecardData?.scorecard?.status === ScorecardStatus.STALE;

export type ScorecardProgressTone = "pass" | "warn" | "fail" | "stale" | "neutral";

export const getScoreProgressTone = (
  score: number | null | undefined,
  thresholds?: { pass?: number; warn?: number },
): ScorecardProgressTone => {
  if (typeof score !== "number") {
    return "neutral";
  }
  const passThreshold = thresholds?.pass ?? SCORECARD_DEFAULT_THRESHOLDS.pass;
  const warnThreshold = thresholds?.warn ?? SCORECARD_DEFAULT_THRESHOLDS.warn;
  if (score >= passThreshold) {
    return "pass";
  }
  if (score >= warnThreshold) {
    return "warn";
  }
  return "fail";
};

export const getVerdictProgressTone = (verdict?: string | null): ScorecardProgressTone => {
  switch (verdict) {
    case ScoreVerdict.PASS:
      return "pass";
    case ScoreVerdict.WARN:
      return "warn";
    case ScoreVerdict.FAIL:
    case ScoreVerdict.ERROR:
      return "fail";
    default:
      return "neutral";
  }
};

const BOOLEAN_STYLE_PRIMITIVES = new Set([
  "CONTAINS",
  "REGEX",
  "ASSERT_VALID",
  "COMPARE",
  "CODE_EXECUTION",
  "MAX_TOOL_CALLS",
  "NO_INVALID_TOOL_CALLS",
  "COUNT",
  "TRAJECTORY",
]);

export interface CriterionResultContext {
  label: string;
  value: string;
  explanation?: string;
}

const LEGACY_GENERIC_STEP_ERROR_PATTERN = /^Backing cell status:/i;
const NUMERIC_ONLY_EVIDENCE_PATTERN = /^[-+]?\d*\.?\d+(?:e[-+]?\d+)?$/i;
const LEGACY_ROW_SCORE_VALUE_EVIDENCE_PATTERN = /^row score has value \([^)]+\)$/i;

export const hasComputedCriterionSummary = (summary?: CriterionSummary): boolean =>
  Boolean(
    summary &&
      (summary.completed_row_count > 0 ||
        summary.error_row_count > 0 ||
        summary.skipped_row_count > 0 ||
        typeof summary.score === "number"),
  );

const isLegacyGenericStepError = (message?: string | null): boolean => {
  const trimmed = message?.trim();
  if (!trimmed) {
    return false;
  }
  return LEGACY_GENERIC_STEP_ERROR_PATTERN.test(trimmed);
};

export const resolveStepEvaluatorError = (
  stepResult?: EvaluatorResult | null,
  backingCell?: Pick<SmartCell, "status" | "display_value" | "value"> | null,
): string | undefined => {
  const storedMessage = stepResult?.error_message?.trim();
  const storedEvidence =
    stepResult?.verdict === ScoreVerdict.ERROR ? stepResult.evidence?.trim() : undefined;
  const stored = storedMessage || storedEvidence;

  const liveError = backingCell
    ? resolveCellDisplayState(backingCell).errorMessage?.trim()
    : undefined;

  if (stored && !isLegacyGenericStepError(stored)) {
    return stored;
  }
  if (liveError) {
    return liveError;
  }
  return undefined;
};

export interface StepEvaluatorErrorState {
  resolvedError?: string;
  hasError: boolean;
  errorDetail?: string;
}

export const getDisplayableEvaluatorEvidence = (evidence?: string | null): string | null => {
  const trimmedEvidence = evidence?.trim();
  if (!trimmedEvidence) {
    return null;
  }
  if (
    NUMERIC_ONLY_EVIDENCE_PATTERN.test(trimmedEvidence) ||
    LEGACY_ROW_SCORE_VALUE_EVIDENCE_PATTERN.test(trimmedEvidence)
  ) {
    return null;
  }
  return trimmedEvidence;
};

export const getStepEvaluatorErrorState = (
  stepResult?: EvaluatorResult | null,
  backingCell?: Pick<SmartCell, "status" | "display_value" | "value"> | null,
): StepEvaluatorErrorState => {
  const resolvedError = resolveStepEvaluatorError(stepResult, backingCell);
  const hasError = stepResult?.verdict === ScoreVerdict.ERROR || Boolean(resolvedError);
  return {
    resolvedError,
    hasError,
    errorDetail: resolvedError ?? (hasError ? stepResult?.evidence : undefined),
  };
};

const resolveStepThresholds = (
  thresholds?: ScorecardThresholds,
): { pass: number; warn: number } => ({
  pass: thresholds?.pass ?? SCORECARD_DEFAULT_THRESHOLDS.pass,
  warn: thresholds?.warn ?? SCORECARD_DEFAULT_THRESHOLDS.warn,
});

export const describeCriterionResultContext = (
  stepResult: EvaluatorResult | null | undefined,
  step: Pick<ScorecardStep, "primitive_type" | "thresholds">,
): CriterionResultContext | null => {
  if (!stepResult) {
    return null;
  }

  const { verdict, score } = stepResult;
  const { pass: passThreshold, warn: warnThreshold } = resolveStepThresholds(step.thresholds);
  const passPct = Math.round(passThreshold * PERCENT_MULTIPLIER);
  const warnPct = Math.round(warnThreshold * PERCENT_MULTIPLIER);

  if (verdict === ScoreVerdict.ERROR || stepResult.error_message) {
    return {
      label: "Result",
      value: "Error",
    };
  }

  if (verdict === ScoreVerdict.SKIPPED) {
    return {
      label: "Result",
      value: "Skipped",
      explanation: "Source data was missing or stale when this row was calculated.",
    };
  }

  const scoreLabel = formatScorecardScore(score);
  const verdictLabel = formatScorecardVerdictLabel(verdict);
  const value = `${scoreLabel} · ${verdictLabel}`;

  const isBooleanStyle =
    BOOLEAN_STYLE_PRIMITIVES.has(step.primitive_type) &&
    typeof score === "number" &&
    (score === 0 || score === 1);

  if (isBooleanStyle) {
    return {
      label: "Result",
      value,
      explanation:
        score === 1 ? "This check passed for this row." : "This check did not pass for this row.",
    };
  }

  let explanation: string | undefined;
  switch (verdict) {
    case ScoreVerdict.PASS:
      explanation = `At or above the pass threshold (≥${passPct}%).`;
      break;
    case ScoreVerdict.WARN:
      explanation = `Below pass (${passPct}%) but at or above review (${warnPct}%).`;
      break;
    case ScoreVerdict.FAIL:
      explanation = `Below the review threshold (<${warnPct}%).`;
      break;
    default:
      explanation = undefined;
  }

  return { label: "Result", value, explanation };
};

/**
 * Describes why a criterion needs attention at the aggregate (all-rows) level,
 * for surfacing errors and failures at the top of the scorecard sidebar
 * without requiring the user to drill into the criterion's row breakdown.
 */
export const describeCriterionSummaryIssue = (
  summary: CriterionSummary,
  thresholds?: { pass?: number; warn?: number },
): string | null => {
  if (summary.verdict === ScoreVerdict.ERROR || summary.error_row_count > 0) {
    const count = summary.error_row_count;
    return count > 0
      ? `${count} row${count === 1 ? "" : "s"} errored during evaluation`
      : "Errored during evaluation";
  }

  const failRowCount = summary.fail_row_count ?? 0;
  if (summary.verdict === ScoreVerdict.FAIL || failRowCount > 0) {
    if (failRowCount > 0) {
      return `${failRowCount} row${failRowCount === 1 ? "" : "s"} failed this evaluator`;
    }
    const warnThreshold = thresholds?.warn ?? SCORECARD_DEFAULT_THRESHOLDS.warn;
    const warnPct = Math.round(warnThreshold * PERCENT_MULTIPLIER);
    return `${formatScorecardScore(summary.score)} — below the review threshold (${warnPct}%)`;
  }

  return null;
};

export const getProgressBarClassName = (tone: ScorecardProgressTone): string =>
  SCORECARD_VERDICT_TONES[tone].progress;

export const getVerdictDotClassName = (verdict?: string | null): string => {
  switch (verdict) {
    case ScoreVerdict.PASS:
      return SCORECARD_VERDICT_TONES.pass.dot;
    case ScoreVerdict.WARN:
      return SCORECARD_VERDICT_TONES.warn.dot;
    case ScoreVerdict.FAIL:
    case ScoreVerdict.ERROR:
      return SCORECARD_VERDICT_TONES.fail.dot;
    default:
      return SCORECARD_VERDICT_TONES.neutral.dot;
  }
};

export const getVerdictBadgeClassName = (verdict?: string | null): string => {
  switch (verdict) {
    case ScoreVerdict.PASS:
      return SCORECARD_VERDICT_TONES.pass.badge;
    case ScoreVerdict.WARN:
      return SCORECARD_VERDICT_TONES.warn.badge;
    case ScoreVerdict.FAIL:
    case ScoreVerdict.ERROR:
      return SCORECARD_VERDICT_TONES.fail.badge;
    default:
      return SCORECARD_VERDICT_TONES.neutral.badge;
  }
};

export const getScorecardHeaderBadge = (
  scorecardData?: GetScorecardResponse | null,
): { label: string; className: string } => {
  const needsSetup =
    scorecardData?.scorecard?.status === ScorecardStatus.NEEDS_SETUP ||
    scorecardData?.scorecard?.steps.length === 0;

  if (needsSetup) {
    return {
      label: "Setup",
      className: "border-border bg-muted/40 text-muted-foreground",
    };
  }
  if (isScorecardStale(scorecardData)) {
    return {
      label: "Stale",
      className: "border-amber-200 bg-amber-50 text-amber-800",
    };
  }
  if (scorecardData?.scorecard?.status === ScorecardStatus.FAILED) {
    return {
      label: "Error",
      className: "border-red-200 bg-red-50 text-red-700",
    };
  }
  if (isScorecardRecalculating(scorecardData)) {
    return {
      label: "Running",
      className: "border-blue-200 bg-blue-50 text-blue-700",
    };
  }
  const verdict = scorecardData?.latest_calculation?.aggregate_verdict;
  if (verdict === ScoreVerdict.SKIPPED) {
    return {
      label: "Setup",
      className: "border-border bg-muted/40 text-muted-foreground",
    };
  }
  if (verdict) {
    return {
      label: formatScorecardVerdictLabel(verdict),
      className: getVerdictBadgeClassName(verdict),
    };
  }
  return {
    label: "Ready",
    className: "border-border bg-muted/40 text-muted-foreground",
  };
};

export const countRowsByVerdict = (rows: ScorecardRowSummary[]) => ({
  all: rows.length,
  pass: rows.filter(row => row.aggregate_verdict === ScoreVerdict.PASS).length,
  warn: rows.filter(row => row.aggregate_verdict === ScoreVerdict.WARN).length,
  fail: rows.filter(
    row =>
      row.aggregate_verdict === ScoreVerdict.FAIL || row.aggregate_verdict === ScoreVerdict.ERROR,
  ).length,
});

export interface ScorecardCriterionRowItem {
  row: ScorecardRowSummary;
  stepResult: EvaluatorResult;
  hasError: boolean;
  resolvedError?: string;
}

export const getScorecardCriterionRowItems = ({
  rows,
  rowBreakdowns,
  step,
  getBackingCell,
}: {
  rows: ScorecardRowSummary[];
  rowBreakdowns: Record<number, GetScorecardRowResponse | undefined>;
  step: Pick<ScorecardStep, "id" | "backing_column_id">;
  getBackingCell?: (
    rowIndex: number,
    backingColumnId: string | null | undefined,
  ) => Pick<SmartCell, "status" | "display_value" | "value"> | undefined;
}): ScorecardCriterionRowItem[] =>
  rows.flatMap(row => {
    const rowBreakdown = rowBreakdowns[row.row_index];
    const stepResult = rowBreakdown?.step_results?.[step.id];
    if (!rowBreakdown || !stepResult) {
      return [];
    }

    const { hasError, resolvedError } = getStepEvaluatorErrorState(
      stepResult,
      getBackingCell?.(row.row_index, step.backing_column_id),
    );

    return [
      {
        row,
        stepResult,
        hasError,
        resolvedError,
      },
    ];
  });

export const matchesCriterionRowFilter = (
  item: Pick<ScorecardCriterionRowItem, "stepResult" | "hasError">,
  filter: ScorecardCriterionRowFilter,
): boolean => {
  switch (filter) {
    case "pass":
      return item.stepResult.verdict === ScoreVerdict.PASS && !item.hasError;
    case "warn":
      return item.stepResult.verdict === ScoreVerdict.WARN && !item.hasError;
    case "fail":
      return item.stepResult.verdict === ScoreVerdict.FAIL && !item.hasError;
    case "error":
      return item.hasError;
    case "all":
    default:
      return true;
  }
};

/**
 * Maps UI verdict filters to the server-side `verdict` query param. "Failed"
 * intentionally includes errored rows so the drill-down matches the fail
 * counts shown on evaluator cards.
 */
export const mapRowVerdictFilterToServerVerdicts = (
  filter: ScorecardRowVerdictFilter,
): string | undefined => {
  switch (filter) {
    case "pass":
      return "pass";
    case "warn":
      return "warn";
    case "fail":
      return "fail,error";
    default:
      return undefined;
  }
};

export const mapCriterionRowFilterToServerVerdicts = (
  filter: ScorecardCriterionRowFilter,
): string | undefined => {
  switch (filter) {
    case "pass":
      return "pass";
    case "warn":
      return "warn";
    case "fail":
      return "fail";
    case "error":
      return "error";
    default:
      return undefined;
  }
};

export interface ScorecardServerVerdictCounts {
  all: number;
  pass: number;
  warn: number;
  fail: number;
  error: number;
}

export const buildVerdictFilterCounts = (
  verdictCounts: Record<string, number> | undefined,
  options?: { includeErrorsInFail?: boolean },
): ScorecardServerVerdictCounts | null => {
  if (!verdictCounts) {
    return null;
  }

  const normalizedVerdictCounts = Object.entries(verdictCounts).reduce<Record<string, number>>(
    (acc, [key, value]) => {
      const normalizedKey = key.trim().toLowerCase();
      if (!normalizedKey) {
        return acc;
      }
      acc[normalizedKey] = (acc[normalizedKey] ?? 0) + value;
      return acc;
    },
    {},
  );

  const pass = normalizedVerdictCounts[ScoreVerdict.PASS] ?? 0;
  const warn = normalizedVerdictCounts[ScoreVerdict.WARN] ?? 0;
  const fail = normalizedVerdictCounts[ScoreVerdict.FAIL] ?? 0;
  const error = normalizedVerdictCounts[ScoreVerdict.ERROR] ?? 0;
  const skipped = normalizedVerdictCounts[ScoreVerdict.SKIPPED] ?? 0;
  const includeErrorsInFail = options?.includeErrorsInFail ?? true;
  return {
    all: pass + warn + fail + error + skipped,
    pass,
    warn,
    fail: includeErrorsInFail ? fail + error : fail,
    error,
  };
};

export const countCriterionRowsByFilter = (items: ScorecardCriterionRowItem[]) => ({
  all: items.length,
  pass: items.filter(item => matchesCriterionRowFilter(item, "pass")).length,
  warn: items.filter(item => matchesCriterionRowFilter(item, "warn")).length,
  fail: items.filter(item => matchesCriterionRowFilter(item, "fail")).length,
  error: items.filter(item => matchesCriterionRowFilter(item, "error")).length,
});

/**
 * A criterion whose result is a single scorecard-level summary (rather than
 * one result per row) -- used to keep "is this criterion row-scoped or
 * scorecard-wide" logic consistent across the overview, header, and row
 * browsing views instead of re-deriving it in each place.
 */
export const isAggregateCriterionShape = (item: {
  primitiveType?: string | null;
  step?: { primitive_type?: string | null } | null;
  summary?: { aggregate_result?: Record<string, unknown> | null } | null;
}): boolean =>
  isColumnAggregatePrimitive(item.primitiveType ?? item.step?.primitive_type) ||
  Boolean(item.summary?.aggregate_result);

/**
 * Carries a per-evaluator row filter over to the all-evaluators row list when
 * switching scope, so the user doesn't lose their place. "Error" has no
 * equivalent at the all-evaluators level (aggregate verdicts don't
 * distinguish an evaluator error from a low score), so it folds into "fail".
 */
export const mapCriterionFilterToRowFilter = (
  filter: ScorecardCriterionRowFilter,
): ScorecardRowVerdictFilter => (filter === "error" ? "fail" : filter);

export const rowMatchesSearchQuery = (
  rowIndex: number,
  search: string,
  getRowLabel?: (rowIndex: number) => string,
): boolean => {
  const query = search.trim().toLowerCase();
  if (!query) {
    return true;
  }
  const label = getRowLabel?.(rowIndex) ?? `Row ${rowIndex + 1}`;
  return label.toLowerCase().includes(query) || String(rowIndex + 1).includes(query);
};

export const getScorecardRowsEmptyMessage = (
  filter: ScorecardCriterionRowFilter,
  hasSearch: boolean,
): string => {
  if (hasSearch) {
    return "No rows match your search.";
  }
  switch (filter) {
    case "fail":
      return "No failed rows.";
    case "warn":
      return "No rows need review.";
    case "pass":
      return "No passing rows yet.";
    case "error":
      return "No evaluator errors.";
    case "all":
    default:
      return "No rows yet.";
  }
};

export const getCriterionSummariesList = (
  scorecardData?: GetScorecardResponse | null,
): CriterionSummary[] => {
  const summaries =
    scorecardData?.criterion_summaries ??
    scorecardData?.display_calculation?.criterion_summaries ??
    scorecardData?.latest_calculation?.criterion_summaries ??
    {};
  return Object.values(summaries);
};

export const getPassCountLabel = (
  rows: ScorecardRowSummary[],
  _latestCalculation?: GetScorecardResponse["latest_calculation"],
  options?: { totalRows?: number; verdictCounts?: Record<string, number> },
): string | null => {
  const serverCounts = buildVerdictFilterCounts(options?.verdictCounts);
  if (serverCounts && serverCounts.all > 0) {
    return `${serverCounts.pass}/${serverCounts.all} pass`;
  }

  const counts = countRowsByVerdict(rows);
  if (counts.all > 0) {
    const denominator = Math.max(counts.all, options?.totalRows ?? 0);
    return `${counts.pass}/${denominator} pass`;
  }
  return null;
};
